import { StaticImageData } from 'next/image';
import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';
import type { QuantityDiscount } from '@/entities/product';

interface CartItem {
  slug: string;
  title: string;
  price: number;
  quantity: number;
  imageUrl: string | StaticImageData;
  color?: string;
  quantityDiscounts?: QuantityDiscount[];
}

type CartState = {
  hydrated: boolean;
  items: CartItem[];
  googleProfile: {
    name: string;
    placeId: string;
  } | null;
};

type CartActions = {
  setHydrated: () => void;
  addItem: (item: CartItem) => void;
  removeItem: (slug: string) => void;
  incItemQuantity: (slug: string) => void;
  decItemQuantity: (slug: string) => void;
  clearCart: () => void;
  setGoogleProfile: (profile: { name: string; placeId: string } | null) => void;
};

type CartStore = CartState & CartActions;

type CartStoreApi = ReturnType<typeof createCartStore>;

const defaultCartState: CartState = {
  hydrated: false,
  items: [],
  googleProfile: null,
};

const initCartStore = (): CartState => defaultCartState;

const createCartStore = (initState: CartState = defaultCartState) =>
  createStore<CartStore>()(
    persist(
      (set) => ({
        ...initState,
        setHydrated: () => set({ hydrated: true }),
        addItem: (item) => {
          set((state) => {
            const items = state.items.slice();
            const index = items.findIndex((i) => i.slug === item.slug);
            if (index > -1) {
              items[index]!.quantity += item.quantity;
            } else {
              items.push({ ...item });
            }
            return { items };
          });
        },
        removeItem: (slug) => {
          set((state) => ({ items: state.items.filter((i) => i.slug !== slug) }));
        },
        incItemQuantity: (slug) => {
          set((state) => {
            const items = state.items.map((item) =>
              item.slug === slug ? { ...item, quantity: item.quantity + 1 } : item,
            );
            return { items };
          });
        },
        decItemQuantity: (slug) => {
          set((state) => {
            const items = state.items.flatMap((item) => {
              if (item.slug !== slug) {
                return item;
              }

              const quantity = item.quantity - 1;

              if (quantity <= 0) {
                return [];
              }

              return { ...item, quantity };
            });
            return { items };
          });
        },
        clearCart: () => set({ items: [] }),
        setGoogleProfile: (profile) => set({ googleProfile: profile }),
      }),
      {
        name: 'cart-storage',
        onRehydrateStorage: () => (state) => {
          state?.setHydrated();
        },
      },
    ),
  );

export { createCartStore, initCartStore };
export type { CartActions, CartItem, CartState, CartStore, CartStoreApi };
